import { useId } from 'react';
import { cn } from '@/lib/cn';

/* ────────────────────────────────────────────────────────────────────────────
   Sparkline — pure inline SVG, no canvas and no charting library.

   The viewBox is the only geometry; `preserveAspectRatio="none"` lets the same
   markup stretch into a 40px card strip or a 160px panel. Strokes carry
   `vectorEffect="non-scaling-stroke"` so that stretch never fattens a line.

   When `floor` is supplied the two tracks share one vertical scale, because the
   whole point is to read the gap between spot and the guaranteed floor.
   ──────────────────────────────────────────────────────────────────────────── */

export interface SparklineProps {
  data: number[];
  /** Matching floor track. Drawn as a second line with a faint band beneath. */
  floor?: number[];
  /**
   * Floor as a fraction of the current price (0..1). Preferred over `floor` for
   * small strips: the band takes the bottom `floorRatio` of the box and the price
   * track scales into what is left, so the price shape stays readable while the
   * band still literally reads as "this much of the price is guaranteed".
   * A shared linear scale can't do both — when the floor is far below the 24h
   * range it pins to the bottom edge and squashes the price into a sliver.
   */
  floorRatio?: number;
  width?: number;
  height?: number;
  tone?: 'auto' | 'up' | 'down' | 'bull' | 'floor';
  area?: boolean;
  strokeWidth?: number;
  className?: string;
}

const TONE_TEXT = {
  up: 'text-up',
  down: 'text-down',
  bull: 'text-bull',
  floor: 'text-floor',
} as const;

const clean = (series: number[]) => series.filter((n) => Number.isFinite(n));

export function Sparkline({
  data,
  floor,
  floorRatio,
  width = 120,
  height = 32,
  tone = 'auto',
  area = true,
  strokeWidth = 1.5,
  className,
}: SparklineProps) {
  const gradientId = useId().replace(/:/g, '');
  const values = clean(data);
  const floors = floor ? clean(floor) : [];

  const first = values[0] ?? 0;
  const last = values[values.length - 1] ?? 0;
  const resolved = tone === 'auto' ? (last >= first ? 'up' : 'down') : tone;

  // Shared vertical scale, padded so the stroke never kisses the edge.
  const pad = Math.max(1.5, strokeWidth);
  const bandRatio =
    typeof floorRatio === 'number' && Number.isFinite(floorRatio)
      ? Math.min(0.7, Math.max(0, floorRatio))
      : null;
  // In band mode the price keeps its own scale above the band; otherwise both
  // tracks share one scale so the absolute gap between them is readable.
  const pool = bandRatio === null ? [...values, ...floors] : values;
  const lo = pool.length ? Math.min(...pool) : 0;
  const hi = pool.length ? Math.max(...pool) : 1;
  const span = hi - lo || Math.abs(hi) || 1;
  const bandTop = bandRatio === null ? height : height - bandRatio * height;
  const top = pad;
  const bottom = bandRatio === null ? height - pad : Math.max(top + 1, bandTop - pad);

  const y = (v: number) => bottom - ((v - lo) / span) * (bottom - top);
  const x = (i: number, n: number) => (n <= 1 ? width / 2 : (i / (n - 1)) * width);

  /** Flat mid-line for a single sample so an empty card still reads as a chart. */
  function line(series: number[]): string {
    if (series.length === 0) return `M 0 ${height / 2} L ${width} ${height / 2}`;
    if (series.length === 1) {
      const flat = y(series[0] as number);
      return `M 0 ${flat} L ${width} ${flat}`;
    }
    return series
      .map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i, series.length).toFixed(2)} ${y(v).toFixed(2)}`)
      .join(' ');
  }

  const linePath = line(values);
  const areaBase = bandRatio === null ? height : bandTop;
  const areaPath = `${linePath} L ${width} ${areaBase} L 0 ${areaBase} Z`;
  const floorPath = bandRatio === null && floors.length ? line(floors) : null;
  const floorBand = floorPath ? `${floorPath} L ${width} ${height} L 0 ${height} Z` : null;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      role="img"
      aria-hidden="true"
      focusable="false"
      className={cn('block w-full overflow-visible', TONE_TEXT[resolved], className)}
    >
      {area ? (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
      ) : null}

      {/* Guaranteed floor first, so the speculative track always sits on top. */}
      {bandRatio !== null && bandRatio > 0 ? (
        <>
          <rect
            x={0}
            y={bandTop}
            width={width}
            height={Math.max(0, height - bandTop)}
            className="fill-floor/12"
          />
          <line
            x1={0}
            y1={bandTop}
            x2={width}
            y2={bandTop}
            className="stroke-floor/80"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        </>
      ) : null}
      {floorBand ? <path d={floorBand} className="fill-floor/10" stroke="none" /> : null}
      {floorPath ? (
        <path
          d={floorPath}
          fill="none"
          className="stroke-floor/85"
          strokeWidth={Math.max(1, strokeWidth - 0.25)}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}

      {area ? <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" /> : null}
      <path
        d={linePath}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
